import React, { useState } from 'react';
import { ArrowLeft, Instagram, Play, Menu, X, ShoppingCart, User, Heart, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Template 14: STUDIO - Photography/Portfolio, Visual-First (Inspired by Shopify's Studio theme)
const Template14 = () => {
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const navLinks = [
        { name: 'Home', path: '/templates/template14' },
        { name: 'Shop', path: '/templates/template14/shop' },
        { name: 'Contact', path: '/templates/template14/contact' },
        { name: 'Orders', path: '/templates/template14/orders' }
    ];

    return (
        <div className="bg-gray-900 min-h-screen text-white">
            {/* Enhanced Header with Full Navigation */}
                        {/* Header with Navigation */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="flex justify-between items-center py-4 lg:py-5">
                        <h1 onClick={() => navigate('/template14')} className="text-2xl lg:text-3xl font-bold text-gray-900 cursor-pointer tracking-tight">
                            MINIMAL
                        </h1>
                        <div className="flex items-center gap-4 lg:gap-6">
                            <Search className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <Heart className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template14/cart')}>
                                <ShoppingCart className="w-5 h-5 text-gray-900 hover:text-gray-600 transition-colors" />
                                <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">0</span>
                            </div>
                            <User className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" onClick={() => navigate('/template14/profile')} />
                        </div>
                    </div>
                    <nav className="border-t border-gray-200">
                        <div className="flex justify-center items-center gap-8 lg:gap-12 py-4">
                            <button onClick={() => navigate('/template14')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Home</button>
                            <button onClick={() => navigate('/template14/shop')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Shop</button>
                            <button onClick={() => navigate('/template14/contact')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Contact</button>
                            <button onClick={() => navigate('/template14/orders')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Orders</button>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Full-Screen Video/Image Hero */}
            <section className="relative h-screen">
                <div className="absolute inset-0">
                    <img
                        src="https://placehold.co/1920x1080/E0E0E0/333333?text=Hero+Image"
                        alt="Hero"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 via-transparent to-gray-900"></div>
                </div>
                <div className="relative h-full flex flex-col justify-end pb-20 px-8">
                    <div className="max-w-4xl">
                        <p className="text-xs tracking-[0.3em] text-gray-300 mb-4">2025 COLLECTION</p>
                        <h2 className="text-6xl md:text-8xl font-light mb-6 leading-none">
                            Visual<br />Storytelling
                        </h2>
                        <p className="text-xl text-gray-300 font-light max-w-2xl">
                            Where fashion meets art. Explore a curated collection designed for those who see beyond the ordinary.
                        </p>
                    </div>
                </div>
            </section>

            {/* Masonry Grid Layout */}
            <section className="px-4 py-20">
                <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
                    {[
                        { height: 'h-96', image: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=600&h=800&fit=crop' },
                        { height: 'h-64', image: 'https://images.unsplash.com/photo-1490114538077-0a7f8cb49891?w=600&h=400&fit=crop' },
                        { height: 'h-80', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=600&h=600&fit=crop' },
                        { height: 'h-96', image: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=600&h=800&fit=crop' },
                        { height: 'h-72', image: 'https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?w=600&h=500&fit=crop' },
                        { height: 'h-64', image: 'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=600&h=400&fit=crop' },
                        { height: 'h-96', image: 'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=600&h=800&fit=crop' },
                        { height: 'h-80', image: 'https://images.unsplash.com/photo-1506629082955-511b1aa562c8?w=600&h=600&fit=crop' }
                    ].map((item, index) => (
                        <div key={index} className="break-inside-avoid group cursor-pointer">
                            <div className={`relative ${item.height} overflow-hidden bg-gray-900`}>
                                <img
                                    src={item.image}
                                    alt={`Gallery ${index + 1}`}
                                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:opacity-75"
                                />
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Play size={48} className="text-white" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Featured Collection - Horizontal Scroll */}
            <section className="py-20 border-t border-gray-800">
                <div className="px-8 mb-12">
                    <h2 className="text-4xl md:text-5xl font-light mb-4">Latest Drops</h2>
                    <p className="text-gray-400">Scroll to explore</p>
                </div>

                <div className="flex overflow-x-auto space-x-6 px-8 pb-4 scrollbar-hide">
                    {[1, 2, 3, 4, 5, 6].map((id) => (
                        <div key={id} className="flex-none w-72 md:w-96 group cursor-pointer">
                            <div className="relative aspect-[3/4] overflow-hidden bg-gray-900 mb-4">
                                <img
                                    src={`https://images.unsplash.com/photo-${1523381210376 + id * 60000}-7cbb3fc41fea?w=800&h=1000&fit=crop`}
                                    alt={`Product ${id}`}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                            </div>
                            <h3 className="text-lg font-light mb-2">Collection Item {id}</h3>
                            <p className="text-sm text-gray-400">$XXX.00</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Full-Width Statement */}
            <section className="relative h-screen">
                <div className="absolute inset-0">
                    <img
                        src="https://placehold.co/1920x1080/E0E0E0/333333?text=Statement"
                        alt="Statement"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/60"></div>
                </div>
                <div className="relative h-full flex items-center justify-center px-8 text-center">
                    <div className="max-w-3xl">
                        <h2 className="text-5xl md:text-7xl font-light mb-8 leading-tight">
                            Designed for<br />The Bold
                        </h2>
                        <p className="text-xl text-gray-300 font-light mb-12">
                            Every piece is a canvas. Every outfit tells a story.
                        </p>
                        <button
                            onClick={() => navigate('/templates/template14/shop')}
                            className="px-12 py-4 border border-white text-white hover:bg-white hover:text-black transition-all font-light tracking-wider"
                        >
                            EXPLORE NOW
                        </button>
                    </div>
                </div>
            </section>

            {/* Product Grid */}
            <section className="bg-gray-50 py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-4">All Products</h2>
                        <p className="text-xl text-gray-600">Discover our latest collection</p>
                    </div>

                    <div className="w-full max-w-none">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-8">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(id => (
                                <div key={id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-2xl hover:-translate-y-2 hover:border-gray-900 transition-all duration-300 group">
                                    <div className="aspect-[4/5] overflow-hidden relative bg-gray-50">
                                        <img
                                            src={`https://placehold.co/400x500/F0F0F0/333333?text=Product+${id}`}
                                            alt={`Product ${id}`}
                                            className="object-cover object-center w-full h-full group-hover:scale-110 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="p-4 lg:p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">Product Title {id}</h3>
                                        <p className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">${(69 + id * 10)}.00</p>
                                        <button
                                            onClick={() => navigate('/templates/template14/cart')}
                                            className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-all duration-300"
                                        >
                                            Add to Cart
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Instagram Feed */}
            <section className="border-t border-gray-700 py-20">
                <div className="px-8 mb-12 text-center">
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <Instagram size={24} />
                        <h2 className="text-2xl font-light tracking-wider">@studio</h2>
                    </div>
                    <p className="text-gray-400">Follow our journey</p>
                </div>

                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 px-2">
                    {[1, 2, 3, 4, 5, 6].map((id) => (
                        <div key={id} className="aspect-square overflow-hidden group cursor-pointer">
                            <img
                                src={`https://images.unsplash.com/photo-${1540518614846 + id * 25000}-e0aa7f38f5e9?w=400&h=400&fit=crop`}
                                alt={`Instagram ${id}`}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                        </div>
                    ))}
                </div>
            </section>

            {/* Newsletter */}
            <section className="border-t border-gray-700 py-20">
                <div className="max-w-2xl mx-auto px-8 text-center">
                    <h3 className="text-3xl font-light mb-4">Join The Studio</h3>
                    <p className="text-gray-400 mb-8">Get exclusive access to new releases and behind-the-scenes content</p>
                    <div className="flex gap-4">
                        <input
                            type="email"
                            placeholder="Email address"
                            className="flex-1 px-6 py-4 bg-transparent border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-white transition-colors"
                        />
                        <button className="px-8 py-4 bg-white text-black font-light tracking-wider hover:bg-gray-200 transition-colors">
                            SUBSCRIBE
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
                        {/* Footer */}
            <footer className="bg-gray-900 text-white mt-0">
                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">MINIMAL</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Contemporary design excellence.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template14')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Home</span></li>
                                <li><span onClick={() => navigate('/template14/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Shop</span></li>
                                <li><span onClick={() => navigate('/template14/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template14/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Orders</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h4>
                            <p className="text-gray-400 text-sm">hello@minimal.com</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8">
                        <p className="text-center text-gray-500 text-sm">© 2025 MINIMAL. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template14;
